import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { NavigationTrigger } from '../../../app/package/base/service/tracking/tracking-service';
import type { Lobby } from '../../base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../base/package/patterns/package/store';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { dispatchCustomEvent } from '../../base/package/util/event';
import styles from './ui-provider-tile.css?inline';

const CName = 'ui-provider-tile';

/**
 * @fires ui-provider-tile-click with provider id
 */
@customElement(CName)
export class UIProviderTile extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Number }) amount? = 0;
  @property({ type: String }) id = '';
  @property({ type: String }) image = '';
  @property({ type: String }) filterId = ''; // Used for tracking only
  @property({ type: String }) slug: string;

  private readonly _subHelper = new SubHelper();

  private _lobby?: Lobby;

  connectedCallback(): void {
    super.connectedCallback();
    this._subHelper.addSub(window.$app.content.getLobby(), (_) => this._updateLobby(_ as Lobby), true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  render() {
    return html`
      <a class="tile" @click=${this._navigateToProvider} href=${this._getPathNavigateToProvider()}
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._navigateToProvider.bind(this, ev))}
      >
        ${this._renderTile()} ${this._renderLink()}
      </a>
    `;
  }

  private _renderTile() {
    return this.image && this.image !== ''
      ? html`<img
          class="img"
          src="${this.image}"
          alt=${this.id}
          crossorigin="anonymous"
          decoding="async"
          fetchpriority="low"
          loading="lazy"
        />`
      : html`<div class="img placeholder"><span class="title">${this.id}</span></div>`;
  }

  private _renderLink() {
    if (!this.amount) {
      return html`<span>…</span>`;
    }
    const label = this.amount > 1 ? window.$app.$t.get(I18n.base.games) : window.$app.$t.get(I18n.base.game);
    return html`<span>${this.amount} ${label}</span>`;
  }

  private _updateLobby(lobby: Lobby) {
    this._lobby = lobby;
  }

  private _navigateToProvider(ev: Event) {
    ev.preventDefault();
    dispatchCustomEvent(this, `${CName}-click`, { id: this.id });

    if (!this.slug) {
      window.$app.logger.log('no slug => cannot navigate to studio category');
      return;
    }

    window.$app.track.navigateTo(NavigationTrigger.GAME_STUDIO, this.filterId);
    // At this point we can be sure that the lobby is loaded and it contains this provider, otherwise this component would not be rendered
    if (this._lobby) {
      window.$app.router.navigateToProvider(this.slug);
    }
  }

  private _getPathNavigateToProvider() {
    return this._lobby && this.slug ? window.$app.router.getPathNavigateToProvider(this.slug) : '/';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIProviderTile;
  }
}
